// =============================================================================
// Header.jsx — top navigation bar
// =============================================================================
// Renders: the teal top bar with role-aware nav, the user's name/email, and a
//          Sign Out button. Nav layout comes from navStructureFor (access.ts):
//          top-level buttons + (for admins) a single "Manage" dropdown grouping
//          Reports / Import / Audit Log / CRM / Feedback. Live counts show on the
//          task-list tabs.
// Item kinds (access.ts): "page" = in-app setPage switch; "tab" = a self-contained
//          module (CRM, Feedback) opened in a NEW browser tab via /<id>.
// Props:
//   page, setPage        - active page state
//   onShowSecurity       - opens MFA/security panel
// Context (not props):
//   user                 - current user object, from useSession
//   logout               - logout handler (Sign Out button), from useSession
//   unactionedTaskCount     - live badge count for Responders task list, from useRequests
//   pickerTaskCount      - live badge count for Pickers task list, from useRequests
// Notes: Header re-renders frequently as counts update via subscription
//        — keep this lean.
// =============================================================================

import { useEffect, useRef, useState } from "react";
import { isNavGroup, navStructureFor } from "../../app/access";
import { useRequests } from "../../app/RequestsContext.jsx";
import { useSession } from "../../app/SessionContext.jsx";
import { confirmNavAway } from "../../services/navGuard";
import { GLYPH } from "../../utils/glyphs";

const TAB_LINK_CLS =
  "text-white no-underline bg-transparent rounded-[6px] py-[6px] px-[14px] text-[13px] font-semibold cursor-pointer flex items-center gap-[5px]";

function Header({ page, setPage, onShowSecurity }) {
  // user/logout + the task counts now come from context rather than props (Phase 3 refactor).
  const { user, logout } = useSession();
  const { unactionedTaskCount, pickerTaskCount, topUpTaskCount } = useRequests();
  // Display arrangement (top-level items + the admin "Manage" dropdown) lives in
  // the single source of truth (src/app/access.ts), shared with portal.jsx's page gates.
  const entries = navStructureFor(user);

  // Which dropdown group is open (by label), if any. Closed on outside-click / Escape.
  const [openGroup, setOpenGroup] = useState(null);
  // Which nested flyout sub-group (e.g. "Unleashed") is open within the Manage
  // dropdown, driven by hover.
  const [openSubGroup, setOpenSubGroup] = useState(null);
  const navRef = useRef(null);
  useEffect(() => {
    if (!openGroup) return;
    function onDocClick(e) {
      if (navRef.current && !navRef.current.contains(e.target)) {
        setOpenGroup(null);
        setOpenSubGroup(null);
      }
    }
    function onKey(e) {
      if (e.key === "Escape") {
        setOpenGroup(null);
        setOpenSubGroup(null);
      }
    }
    document.addEventListener("mousedown", onDocClick);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDocClick);
      document.removeEventListener("keydown", onKey);
    };
  }, [openGroup]);

  // A single nav item — a new-tab link ("tab") or an in-app page button ("page").
  function renderItem(item, inDropdown) {
    if (item.kind === "tab") {
      // The shelter's top-level Client Feedback button gets a thumbs-up + a 40px
      // left shift (John, 2026-06). Admin's Feedback (in the Manage dropdown) stays plain.
      const isTopFeedback = item.id === "feedback" && !inDropdown;
      const cls = inDropdown
        ? "block w-full text-left no-underline py-2 px-3 text-[13px] text-zephyr-text hover:bg-black/5"
        : `${TAB_LINK_CLS}${isTopFeedback ? " ml-[40px] border border-white/60 hover:bg-white/10" : ""}`;
      return (
        <a
          key={item.id}
          href={`/${item.id}`}
          target="_blank"
          rel="noopener noreferrer"
          onClick={() => setOpenGroup(null)}
          className={cls}
          style={{ fontFamily: "inherit" }}
        >
          {isTopFeedback ? "Client Feedback on Support Received" : item.label}
          {isTopFeedback && <span aria-hidden="true">{GLYPH.thumbsUp}</span>}
        </a>
      );
    }
    const active = page === item.id;
    // Rendered as a real link (href to the screen's route) so the browser offers
    // "Open in new tab" / middle-click / Ctrl-click natively. A plain left-click is
    // intercepted for in-app SPA navigation (with the unsaved-changes guard); a
    // modifier / middle click falls through to the browser's new-tab handling.
    const cls = inDropdown
      ? [
          "block w-full text-left no-underline border-none py-2 px-3 text-[13px] cursor-pointer",
          active ? "bg-black/10 text-zephyr-text font-semibold" : "bg-transparent text-zephyr-text",
        ].join(" ")
      : [
          "text-white no-underline border-none rounded-[6px] py-[6px] px-[14px] text-[13px] font-semibold cursor-pointer flex items-center gap-[5px]",
          active ? "bg-white/20" : "bg-transparent",
        ].join(" ");
    return (
      <a
        key={item.id}
        href={`/${item.id}`}
        onClick={async (e) => {
          // Let the browser handle new-tab/window intents on the real link.
          if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button === 1) return;
          e.preventDefault();
          setOpenGroup(null);
          // A screen with unsaved edits (navGuard) gets a Stay / Leave & discard
          // confirm before the page switches.
          if (!(await confirmNavAway())) return;
          setPage(item.id);
        }}
        className={cls}
        style={{ fontFamily: "inherit" }}
      >
        {item.label}
        {item.id === "todo" && unactionedTaskCount > 0 && (
          <span className="bg-[#E53935] text-white rounded-[10px] py-px px-[6px] text-[10px] font-bold">
            {unactionedTaskCount}
          </span>
        )}
        {item.id === "pickers" && pickerTaskCount > 0 && (
          <span className="bg-[#E53935] text-white rounded-[10px] py-px px-[6px] text-[10px] font-bold">
            {pickerTaskCount}
          </span>
        )}
        {item.id === "top_up_tasks" && topUpTaskCount > 0 && (
          <span className="bg-[#E53935] text-white rounded-[10px] py-px px-[6px] text-[10px] font-bold">
            {topUpTaskCount}
          </span>
        )}
      </a>
    );
  }

  // Whether the active page lives inside a Manage entry (a plain item OR a nested
  // sub-group's items) — highlights the Manage button when a child screen is open.
  function entryHasActivePage(i) {
    if (isNavGroup(i)) {
      if (i.groupScreen && i.groupScreen.id === page) return true;
      return i.items.some((s) => s.kind === "page" && s.id === page);
    }
    return i.kind === "page" && i.id === page;
  }

  // A child of the Manage dropdown: either a plain item, or a flyout sub-group
  // (a ▸ heading whose panel opens to the side on hover — e.g. "Unleashed" →
  // Sales Orders). A sub-group whose heading is itself a page (DFV Services,
  // Zephyr Staff) navigates on click AND flies out its tools.
  function renderManageChild(item) {
    if (!isNavGroup(item)) return renderItem(item, true);
    const subOpen = openSubGroup === item.group;
    return (
      // biome-ignore lint/a11y/noStaticElementInteractions: hover-only flyout reveal; the real interactive controls are the heading button + the items inside
      <div
        key={item.group}
        className="relative"
        data-testid={`manage-subgroup-${item.group}`}
        onMouseEnter={() => setOpenSubGroup(item.group)}
        onMouseLeave={() => setOpenSubGroup(null)}
      >
        {item.groupScreen ? (
          <div className="flex items-center">
            <div className="flex-1">{renderItem(item.groupScreen, true)}</div>
            <span className="pr-2 text-[20px] leading-none text-zephyr-text/50">▸</span>
          </div>
        ) : (
          <div className="flex items-center justify-between py-2 px-3 text-[13px] text-zephyr-text cursor-default">
            <span>{item.group}</span>
            <span className="text-[20px] leading-none text-zephyr-text/50">▸</span>
          </div>
        )}
        {subOpen && (
          <div className="absolute left-full top-0 min-w-[190px] bg-white rounded-[8px] shadow-[0_4px_16px_rgba(0,0,0,0.2)] py-1 z-[120]">
            {/* Repeat the heading's own screen inside the flyout so it's clear it's
                clickable (not only via the heading). */}
            {item.groupScreen && (
              <>
                {renderItem(item.groupScreen, true)}
                <div className="my-1 border-t border-zephyr-border/40" />
              </>
            )}
            {item.items.map((sub) => renderItem(sub, true))}
          </div>
        )}
      </div>
    );
  }

  // A dropdown group (e.g. "Manage"): a toggle button + an absolutely-positioned panel.
  function renderGroup(grp) {
    const open = openGroup === grp.group;
    const activeWithin = grp.items.some(entryHasActivePage);
    return (
      <div key={grp.group} className="relative">
        <button
          type="button"
          aria-haspopup="true"
          aria-expanded={open}
          onClick={() => {
            setOpenGroup(open ? null : grp.group);
            setOpenSubGroup(null);
          }}
          className={[
            "text-white border-none rounded-[6px] py-[6px] px-[14px] text-[13px] font-semibold cursor-pointer flex items-center gap-[5px]",
            open || activeWithin ? "bg-white/20" : "bg-transparent",
          ].join(" ")}
          style={{ fontFamily: "inherit" }}
        >
          {grp.group}
          <span className="text-[9px] leading-none">{open ? "▲" : "▼"}</span>
        </button>
        {open && (
          <div className="absolute left-0 top-[calc(100%+4px)] min-w-[190px] bg-white rounded-[8px] shadow-[0_4px_16px_rgba(0,0,0,0.2)] py-1 z-[110]">
            {grp.items.map((item) => renderManageChild(item))}
          </div>
        )}
      </div>
    );
  }

  return (
    <header className="bg-zephyr-teal-dark text-white shadow-[0_2px_8px_rgba(0,0,0,0.2)] sticky top-0 z-[100]">
      <div className="max-w-[var(--page-max)] mx-auto px-6 flex items-center justify-between h-[60px]">
        <div className="flex items-center gap-5">
          <span className="font-bold text-[18px]">Zephyr Education</span>
          <nav ref={navRef} className="flex gap-1 items-center">
            {entries.map((entry) =>
              isNavGroup(entry) ? renderGroup(entry) : renderItem(entry, false)
            )}
          </nav>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={onShowSecurity}
            className="text-right bg-white/10 border border-white/20 rounded-[8px] py-[6px] px-[10px] cursor-pointer text-white"
            style={{ fontFamily: "inherit" }}
          >
            <div className="text-[13px] font-semibold">
              {user.displayName || user.shelterName}
              {user.sessionMode && (
                <span className="ml-2 bg-white/20 rounded-[3px] py-px px-[5px] text-[10px] font-bold">
                  {user.sessionMode.charAt(0).toUpperCase() + user.sessionMode.slice(1)}
                </span>
              )}
            </div>
            <div className="text-[11px] opacity-70">
              {user.shelterCode && (
                <span className="mr-[6px] bg-white/20 rounded-[3px] py-px px-[5px]">
                  {user.shelterCode}
                </span>
              )}
              {user.email}
            </div>
          </button>
          <button
            onClick={async () => {
              if (!(await confirmNavAway())) return;
              logout();
            }}
            className="bg-white/15 text-white border border-white/30 rounded-[6px] py-[6px] px-3 text-xs cursor-pointer"
            style={{ fontFamily: "inherit" }}
          >
            Sign Out
          </button>
        </div>
      </div>
    </header>
  );
}

export { Header };
